import React from "react";
import AttendType from "../../models/AttendType";
import {Avatar, Box, Grid, ListItem} from "@mui/material";
import {HILIGHT_COLOR} from "../../constants/constants";
import {AlfyCircularButton} from "../AlfyCircularButton";
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import Member from "../../models/Member";
import {useRecoilValue, useSetRecoilState} from "recoil";
import {isSameDate} from "../../utils/Utils";
import {useKakao} from "../../hooks/kakaoHooks";
import {
    bottomDrawerPollInfoState,
    openBottomDrawerState
} from "../../states/appointment/overviewAtom";
import {useFetchAppointmentWithPoll} from "../../states/appointment/server";
import {useParams} from "react-router-dom";
import {useFetchMemberMe} from "../../states/member/server";

interface AppointmentBottomDrawerListItemSpec {
    member: Member
    attendType: AttendType
    withAskAttendanceButton?: boolean
}

export const AppointmentBottomDrawerListItem: React.FC<AppointmentBottomDrawerListItemSpec> =
    ({ member, attendType, withAskAttendanceButton = true }) => {
        const { askAttendance } = useKakao()
        const setOpenBottomDrawer = useSetRecoilState(openBottomDrawerState)
        const { id: appointmentId } = useParams()
        const { data: memberMe } = useFetchMemberMe(appointmentId!)
        const { data: appointment } = useFetchAppointmentWithPoll(appointmentId!)
        const selectedDatePollInfo = useRecoilValue(bottomDrawerPollInfoState)

        const handleAskAttendance = (member: Member) => {
            // TODO 변환하고 난리도 아니다. 뭔가... pollItemId로 통일하는 조치를 취해야 한다
            const targetPollItem = appointment!.pollItems.find(pollItem => isSameDate(pollItem.date, selectedDatePollInfo!.date))
            askAttendance(member.name, targetPollItem!)
            setOpenBottomDrawer(false)
        }

        return (
            <ListItem key={member.id} disablePadding>
                <Box paddingTop={'12px'} paddingBottom={'12px'} width={'100%'}>
                <Grid container spacing={1}>
                    <Grid item>
                        <CustomAvatar src={member.profileImage} attendType={attendType}/>
                    </Grid>
                    <Grid item container direction={'column'} flex={1}>
                        <Grid item>
                            <span style={{color: getNameTextColorByAttendType(attendType)}}>{member.name}</span>
                        </Grid>
                        <Grid item>
                            <span style={{color: getTextColorByAttendType(attendType), fontSize: '11px'}}>{getVoteStatusText(attendType)}</span>
                        </Grid>
                    </Grid>
                    {
                        withAskAttendanceButton && attendType === AttendType.NOT_SURE && memberMe?.id !== member.id &&
                        <Grid item>
                            <AlfyCircularButton text={"되는지 물어보기"} onClick={() => handleAskAttendance(member)} />
                        </Grid>
                    }
                </Grid>
                </Box>
            </ListItem>
        );

        function getNameTextColorByAttendType(attendType: AttendType): string {
            switch (attendType) {
                case AttendType.ATTEND:
                case AttendType.NOT_SURE:
                    return '#555555';
                case AttendType.ABSENT:
                    return '#A7A5A5';
                default:
                    throw new Error("정의되지 않은 참여타입입니다.")
            }
        }

        function getTextColorByAttendType(attendType: AttendType): string {
            switch (attendType) {
                case AttendType.ATTEND:
                    return HILIGHT_COLOR;
                case AttendType.ABSENT:
                    return '#A7A5A5';
                case AttendType.NOT_SURE:
                    return '#555555';
                default:
                    throw new Error("정의되지 않은 참여타입입니다.")
            }
        }

        function getVoteStatusText(attendType: AttendType): string {
            switch (attendType) {
                case AttendType.ATTEND:
                    return "확실히 돼요";
                case AttendType.ABSENT:
                    return "이 날은 안돼요";
                case AttendType.NOT_SURE:
                    return "투표 안함";
                default:
                    throw new Error("정의되지 않은 참여타입입니다.")
            }
        }
    }

interface CustomAvatarProps {
    src: string;
    attendType: AttendType;
}

const CustomAvatar: React.FC<CustomAvatarProps> = ({ src, attendType, }) => {
    return (
    <Box position="relative">
        <Avatar
            src={src}
            sx={{
                width: 40,
                height: 40,
                ...((attendType === AttendType.NOT_SURE || attendType === AttendType.ABSENT) && {
                    opacity: 0.5
                })
            }}
        />
        {attendType === AttendType.NOT_SURE && (
            <Box
                position="absolute"
                top="50%"
                left="50%"
                sx={{
                    transform: 'translate(-50%, -50%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1
                }}
            >
                <QuestionMarkIcon
                    sx={{
                        color: '#555555',
                        fontSize: '24px'
                    }}
                />
            </Box>
        )}
    </Box>
    );
}